import { createBrowserRouter, Outlet } from "react-router-dom";
import { lazy } from "react";
import { AdminLayout } from "@/layout/admin-layout";
import { ProtectedRoute } from "@/routes/protected-route";
import { DeviceGuard } from "@/components/guard/device-guard";
import * as paths from './const';

// 공통
const LoginPage = lazy(() => import("@/pages/login"));
const HomePage = lazy(() => import("@/pages/home"));
const NotFoundPage = lazy(() => import("@/pages/notFound"));

// 상품(디자인)
const ProductsPiecesPage = lazy(() => import("@/pages/products/pieces"));
const ProductPieceFormPage = lazy(() => import("@/pages/products/pieces/form"));
const ProductsSetPage = lazy(() => import("@/pages/products/set"));
const ProductSetFormPage = lazy(() => import("@/pages/products/set/form"));
const ProductClassificationPage = lazy(() => import("@/pages/products/classification"));

// DIY
const DiyPalettePage = lazy(() => import("@/pages/diy/palette"));
const DiyPatternPage = lazy(() => import("@/pages/diy/pattern"));
const DiyStickerPage = lazy(() => import("@/pages/diy/sticker"));

// 액세서리
const AccessoryPartsPage = lazy(() => import("@/pages/accessory/parts"));
const AccessoryPartsCreatePage = lazy(() => import("@/pages/accessory/parts/create"));
const AccessoryPartsEditPage = lazy(() => import("@/pages/accessory/parts/edit"));
const AccessoryConsumablesPage = lazy(() => import("@/pages/accessory/consumables"));
const AccessoryConsumablesCreatePage = lazy(() => import("@/pages/accessory/consumables/create"));
const AccessoryConsumablesEditPage = lazy(() => import("@/pages/accessory/consumables/edit"));
const AccessoryNonSalesPage = lazy(() => import("@/pages/accessory/non-sales"));

// 고객
const CustomerPage = lazy(() => import("@/pages/customer"));
const CustomerDetailPage = lazy(() => import("@/pages/customer/detail"));

// 주문
const OrderPage = lazy(() => import("@/pages/order"));
const OrderDetailPage = lazy(() => import("@/pages/order/detail"));

// 결제
const PaymentPage = lazy(() => import("@/pages/payment"));

// 매장
const StorePage = lazy(() => import("@/pages/store"));
const StoreDetailPage = lazy(() => import("@/pages/store/detail"))
const StoreCreatePage = lazy(() => import("@/pages/store/form/create"));
const StoreEditPage = lazy(() => import("@/pages/store/form/edit"));

// 장비
const EquipmentVendingMachineListPage = lazy(() => import("@/pages/equipment/vending-machine"));
const EquipmentVendingMachineDetailPage = lazy(() => import("@/pages/equipment/vending-machine/detail"));
const EquipmentVendingMachineFormPage = lazy(() => import("@/pages/equipment/vending-machine/pc/form"));

// 기획전
const SpecialExhibitionPage = lazy(() => import("@/pages/special-exhibition"));
const SpecialExhibitionFormPage = lazy(() => import("@/pages/special-exhibition/form"));

export const router = createBrowserRouter([
  {
    path: paths.ROUTE_LOGIN,
    element: <LoginPage />,
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          // Responsive (PC/MO 공통)
          {
            index: true,
            element: <HomePage />,
          },

          // Adaptive (PC/MO 적응형)
          {
            path: paths.ROUTE_EQUIPMENT_VENDING_MACHINE,
            element: <EquipmentVendingMachineListPage />,
          },
          {
            path: `${paths.ROUTE_EQUIPMENT_VENDING_MACHINE}/:id/detail`,
            element: <EquipmentVendingMachineDetailPage />,
          },

          // PC Only (모바일 접근 차단)
          {
            element: <DeviceGuard><Outlet /></DeviceGuard>,
            children: [
              // 상품(디자인)
              {
                path: paths.ROUTE_PRODUCTS_PIECES,
                element: <ProductsPiecesPage />,
              },
              {
                path: `${paths.ROUTE_PRODUCTS_PIECES}/create`,
                element: <ProductPieceFormPage key="create" />,
              },
              {
                path: `${paths.ROUTE_PRODUCTS_PIECES}/:id/edit`,
                element: <ProductPieceFormPage key="edit" />,
              },
              {
                path: paths.ROUTE_PRODUCTS_SET,
                element: <ProductsSetPage />,
              },
              {
                path: `${paths.ROUTE_PRODUCTS_SET}/create`,
                element: <ProductSetFormPage key="create" />,
              },
              {
                path: `${paths.ROUTE_PRODUCTS_SET}/:id/edit`,
                element: <ProductSetFormPage key="edit" />,
              },
              {
                path: paths.ROUTE_PRODUCTS_CLASSIFICATION,
                element: <ProductClassificationPage />,
              },

              // DIY
              {
                path: paths.ROUTE_DIY_PALETTE,
                element: <DiyPalettePage />,
              },
              {
                path: paths.ROUTE_DIY_PATTERN,
                element: <DiyPatternPage />,
              },
              {
                path: paths.ROUTE_DIY_STICKER,
                element: <DiyStickerPage />,
              },

              // 액세서리
              {
                path: paths.ROUTE_ACCESSORIES_PARTS,
                element: <AccessoryPartsPage />,
              },
              {
                path: `${paths.ROUTE_ACCESSORIES_PARTS}/create`,
                element: <AccessoryPartsCreatePage />,
              },
              {
                path: `${paths.ROUTE_ACCESSORIES_PARTS}/:id/edit`,
                element: <AccessoryPartsEditPage />,
              },
              {
                path: paths.ROUTE_ACCESSORIES_CONSUMABLES,
                element: <AccessoryConsumablesPage />,
              },
              {
                path: `${paths.ROUTE_ACCESSORIES_CONSUMABLES}/create`,
                element: <AccessoryConsumablesCreatePage />,
              },
              {
                path: `${paths.ROUTE_ACCESSORIES_CONSUMABLES}/:id/edit`,
                element: <AccessoryConsumablesEditPage />,
              },
              {
                path: paths.ROUTE_ACCESSORIES_NONSALES,
                element: <AccessoryNonSalesPage />
              },
              
              // 고객
              {
                path: paths.ROUTE_CUSTOMER,
                element: <CustomerPage />,
              },
              {
                path: `${paths.ROUTE_CUSTOMER}/:id/detail`,
                element: <CustomerDetailPage />,
              },

              // 주문
              {
                path: paths.ROUTE_ORDER,
                element: <OrderPage />,
              },
              {
                path: `${paths.ROUTE_ORDER}/:id/detail`,
                element: <OrderDetailPage />
              },

              // 결제
              {
                path: paths.ROUTE_PAYMENT,
                element: <PaymentPage />,
              },

              // 매장
              {
                path: paths.ROUTE_STORE,
                element: <StorePage />
              },
              {
                path: `${paths.ROUTE_STORE}/:id/detail`,
                element: <StoreDetailPage />
              },
              {
                path: `${paths.ROUTE_STORE}/create`,
                element: <StoreCreatePage key="create" />,
              },
              {
                path: `${paths.ROUTE_STORE}/:id/edit`,
                element: <StoreEditPage key="edit" />,
              },

              // 장비
              {
                path: `${paths.ROUTE_EQUIPMENT_VENDING_MACHINE}/create`,
                element: <EquipmentVendingMachineFormPage key="create" />,
              },
              {
                path: `${paths.ROUTE_EQUIPMENT_VENDING_MACHINE}/:id/edit`,
                element: <EquipmentVendingMachineFormPage key="edit" />,
              },

              // 기획전
              {
                path: paths.ROUTE_SPECIAL_EXHIBITION,
                element: <SpecialExhibitionPage />,
              },
              {
                path: `${paths.ROUTE_SPECIAL_EXHIBITION}/create`,
                element: <SpecialExhibitionFormPage key="create" />,
              },
              {
                path: `${paths.ROUTE_SPECIAL_EXHIBITION}/:id/edit`,
                element: <SpecialExhibitionFormPage key="edit" />,
              },
            ],
          },

          // 404 페이지 (PC/MO 공통)
          {
            path: '*',
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);
